import { Component, isDevMode, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { debounceTime, finalize, map, repeat, takeUntil } from 'rxjs/operators';
import { MyStudy, MyStudyMetadata, NgChartsBarPlotMetadata, ObservationsMetadata } from './my-study';
import { Subject } from 'rxjs';
import { MyStudiesService } from './my-studies.service';
import { Pageable } from '../../shared/model/pageable';
import { UrlService } from '../../shared/service/url.service';
import { ProgramContext } from './program.context';
import { TranslateService } from '@ngx-translate/core';
import { STUDIES_EDITION_PERMISSIONS } from '../../shared/auth/permissions';
import { ScaleType } from '@swimlane/ngx-charts';
import { SubscriptionsManager } from '../../shared/util/subscriptions-manager';
import { translate } from '@angular/localize/tools';

@Component({
    selector: 'jhi-my-studies',
    templateUrl: './my-studies.component.html'
})
export class MyStudiesComponent implements OnDestroy {

    readonly MAX_ENVIRONMENTS_TO_SHOW = 10;

    STUDIES_EDITION_PERMISSIONS = [...STUDIES_EDITION_PERMISSIONS];

    onStudySelect = new Subject();
    onStudyDeselect = new Subject();

    // ngx-charts options

    /*
     * FIXME dev mode issues (not happening in prod)
     *  - responsive (view=undefined) (use default->600,400 as workaround)
     *   https://github.com/swimlane/ngx-charts/issues/374
     *  - ERROR TypeError: Cannot read property 'runOutsideAngular' of undefined
     *  - tooltip icons not rendering correctly
     *  - PLEASE NOTE in dev mode the chart will overflow in small screens
     */
    view: [number, number] = isDevMode() ? [600, 400] : undefined;
    showXAxis = true;
    showYAxis = true;
    gradient = false;
    showLegend = true;
    showXAxisLabel = true;
    xAxisLabel = 'program.my.studies.x-axis-label';
    showYAxisLabel = true;
    yAxisLabel = 'program.my.studies.y-axis-label';
    legendLabel = 'program.my.studies.legend-label';
    animations = true;
    colorScheme = {
        name: 'custom',
        group: ScaleType.Ordinal,
        selectable: true,
        domain: ['#f7912f', '#f7b62f', '#ffffff']
    };

    studies: MyStudy[];
    study: MyStudy;

    page = 1;
    pageSize = 5;
    predicate = 'startDate';
    reverse = false;
    totalCount: any;
    isLoading = false;

    subscriptionManager = new SubscriptionsManager();
    studyMetaDataSubscriptionManager = new SubscriptionsManager();

    constructor(
        private route: ActivatedRoute,
        private myStudiesService: MyStudiesService,
        public urlService: UrlService,
        public context: ProgramContext,
        private translateService: TranslateService
    ) {
        this.subscriptionManager.add = this.route.queryParams.subscribe((params) => {
            // params['programUUID'] just to listen for changes
            this.load();
        });

        this.subscriptionManager.add = this.onStudySelect.pipe(
            debounceTime(100),
            takeUntil(this.onStudyDeselect),
            repeat()
        ).subscribe((study: MyStudy) => {
            this.select(study);
        });
    }

    ngOnDestroy(): void {
        this.subscriptionManager.unsubscribeAll();
        this.studyMetaDataSubscriptionManager.unsubscribeAll();
    }

    load() {
        this.isLoading = true;
        this.subscriptionManager.add = this.myStudiesService.getMyStudies(
            <Pageable>({
                page: this.page - 1,
                size: this.pageSize,
                sort: this.getSort()
            }),
            this.context.program.crop,
            this.context.program.uniqueID
        ).pipe(map((resp) => {
            this.totalCount = resp.headers.get('X-Total-Count');
            return resp.body.map((study) => <MyStudy>({
                studyId: study.studyId,
                name: study.name,
                type: study.type,
                date: study.date,
                folder: study.folder,
                metadata: {}
            }));
        })).pipe(
            finalize(() => this.isLoading = false)
        ).subscribe((studies) => {
            this.studies = studies;
        });
    }

    sort() {
        this.page = 1;
        this.load();
    }

    getSort() {
        if (!this.predicate) {
            return '';
        }
        return [this.predicate + ',' + (this.reverse ? 'asc' : 'desc')];
    }

    private transformMetadata(observations: ObservationsMetadata[]) {
        if (!observations || observations.length === 0) {
            return {};
        }
        const aggregatedDatasetsByEnvName: { [key: string]: ObservationsMetadata } =
            observations.reduce((prev: { [key: string]: ObservationsMetadata }, curr: ObservationsMetadata) => {
                const prevVal = prev[curr.instanceName];
                if (!prevVal) {
                    prev[curr.instanceName] = Object.assign({}, curr);
                } else {
                    prevVal.confirmedCount += curr.confirmedCount;
                    prevVal.pendingCount += curr.pendingCount;
                    prevVal.unobservedCount += curr.unobservedCount;
                }
                return prev;
            }, {});
        const hasMoreEnvironments = Object.values(aggregatedDatasetsByEnvName).length > this.MAX_ENVIRONMENTS_TO_SHOW;
        const observationSeries = Object.values(aggregatedDatasetsByEnvName)
            .slice(0, this.MAX_ENVIRONMENTS_TO_SHOW)
            .map((obs: ObservationsMetadata) => {
                return <NgChartsBarPlotMetadata>({
                    name: obs.instanceName,
                    series: [{
                        name: this.translateService.instant('program.my.studies.legend.confirmed'),
                        value: obs.confirmedCount
                    }, {
                        name: this.translateService.instant('program.my.studies.legend.pending'),
                        value: obs.pendingCount
                    }, {
                        name: this.translateService.instant('program.my.studies.legend.unobserved'),
                        value: obs.unobservedCount
                    }]
                });
            });
        return <MyStudyMetadata>({
            observations: observationSeries,
            hasMoreEnvironments
        });
    }

    onStudyClick(study) {
        this.onMouseLeave();
        if (study && !study.metadata.observations) {
            this.onStudySelect.next(study);
            this.studyMetaDataSubscriptionManager.add = this.myStudiesService.getStudyMetaData(this.context.program.crop, study.studyId).subscribe((response) => {
                study.metadata = this.transformMetadata(response.body);
            });
        } else {
            this.onStudySelect.next(study);
        }
    }

    onMouseLeave() {
        this.studyMetaDataSubscriptionManager.unsubscribeAll();
        this.onStudyDeselect.next();
    }

    select(study: MyStudy) {
        if (study) {
            this.studies.forEach((s) => s.selected = false);
            study.selected = true;
            this.study = study;
        }
    }
}
